// ModalTextArea.tsx
import React, {useRef, useState} from "react";
import {Label, Flag, Modal} from "semantic-ui-react";
import {IAiMeta, IDataTwoLanguageInput} from "../../VirtualizedTableInterfaces";
import {plainTextToQuillHtml} from "./EditorUtil";
import {QuillEditor} from "./QuilEditor";
import HATEOASClient from "src/services/HATEOASClient";
import {OptionAWSTranslateDto, ResourceListOfOptionDto} from "src/services/Client";
import PreviewScreen from "./PreviewScreen";
import TranslateIconButton from "./TranslateIconButton";
import {handleBackToEdit, handleConfirm, useGenericPreviewHandler} from "./AWSTranslatePreviewHandler";
export interface ApiResult {
    text: string;
    meta?: {changes?: number; confidence?: number;[k: string]: any};
}

export const buildPayload = (value: any): OptionAWSTranslateDto => {
    if (Array.isArray(value)) {
        const germanObj = value.find(v => v.flag === "de");
        const cleanText = germanObj && germanObj.value ? germanObj.value : "";
        return { text: cleanText };
    }
    if (value && typeof value === "object") {
        const valueArr = Object.keys(value).map(key => value[key]);
        const germanObj = valueArr.find((v: any) => v.flag === "de");
        const cleanText = germanObj && germanObj.value ? germanObj.value : "";
        return { text: cleanText };
    } else if (value && typeof value === "number") {
        return { objectId: value } as any;
    }
    return { text: "" };
};

export const extractJSONFromResponse = (text: string) => {
    if (!text) return '';
    let cleaned = text.replace(/```json\s*|\s*```/g, '').trim();

    try {
        const parsed = JSON.parse(cleaned);
        return (parsed && typeof parsed === 'object' && parsed.text) ? String(parsed.text) : String(parsed);
    } catch (e) {
        return cleaned.replace(/\\n/g, '\n').replace(/\\"/g, '"');
    }
};

interface ModalTextAreaOwnProps {
    value: IDataTwoLanguageInput | null;
    focusKey: string;
    readOnly: boolean;
    onChange: (key: string, value: string, aiMeta?: IAiMeta) => void;
    dialogHeader?: {
        dialogTitle: string;
        previewAiHeader?: string;
        previewAiSubHeader?: string;
        previewAiOriginal?: string;
        previewAiPreview?: string;
        previewAiConfirm?: string;
        previewAiCancel?: string;
        previewAiConfimChange?: string;
        previewAiGrammarSubHeader?:string;
        previewAiGenerateSubHeader?: string;
        tooltipAiTranslate?: string;
        tooltipAiGenerate?: string;
        tooltipAiGrammar?: string;
        editingColumn: string;
        optionType: string;
        optionCode: string;
        pdipText: string;
    };
    longTextSuggestion?: boolean;
    closeOnDimmerClick: (value: boolean) => void;
    isEditor?: boolean;
    isNewGrid?: boolean;
    onConfirm?: (finalData: {confirmed: boolean; data: any}) => void;
    onCancel?: () => void;
    client?: HATEOASClient;
    postOptions?: ResourceListOfOptionDto;
    apiMethod?: (client: any, postOptions: any) => {execute: (payload: any) => Promise<any>};
    grammarApiMethod?: (client: any, postOptions: any) => {execute: (payload: any) => Promise<any>};
    suggestionApiMethod?: (client: any, postOptions: any) => {execute: (payload: any) => Promise<any>};
    objectId?:number;
}

type Step = "edit" | "preview";

const ModalTextArea: React.FC<ModalTextAreaOwnProps> = (props) => {
    const refInitialFocus = useRef<any>(null);
    const [step, setStep] = React.useState<Step>("edit");
    const [reviewChecked, setReviewChecked] = React.useState(false);
    const [isSuggestionMode, setIsSuggestionMode] = React.useState(false);

    // Use the generic preview handler
    const {
    loading: translateLoading,
    error: translateError,
    apiResult: translateResult,
    requestPreview: requestPreview
    } = useGenericPreviewHandler(props);

    const {
    loading: suggestionLoading,
    error: suggestionError,
    apiResult: suggestionResult,
    requestPreview: requestSuggestionPreview
    } = useGenericPreviewHandler(props);

    const handleMouseRelease = (e: any) => {
        props.closeOnDimmerClick(true);
    };
    const getGbKey = () => {
        if (!props.value) return "1";
        return Object.keys(props.value).find(key => props.value![key].flag === "gb") || "1";
    };
    const getDeKey = () => {
        if (!props.value) return "0";
        return Object.keys(props.value).find(key => props.value![key].flag === "de") || "0";
    };
    const {
        previewAiHeader = undefined,
        previewAiSubHeader = undefined,
        previewAiOriginal = undefined,
        previewAiPreview = undefined,
        previewAiConfirm = undefined,
        previewAiCancel = undefined,
        previewAiConfimChange = undefined,
        previewAiGrammarSubHeader = undefined,
        previewAiGenerateSubHeader = undefined,
        tooltipAiTranslate =undefined,
        tooltipAiGrammar =undefined,
        tooltipAiGenerate = undefined
    } = props.dialogHeader || {};

    const [activeGrammarKey, setActiveGrammarKey] = useState<string | null>(null);
    const {
        loading: grammarLoading,
        error: grammarError,
        apiResult: grammarResult,
        requestPreview: requestGrammarPreview,
    } = useGenericPreviewHandler(props);

    const handleGrammarCheck = (key: string) => {
        const { client, postOptions, grammarApiMethod, value } = props;

        if (client && postOptions && grammarApiMethod && value) {
        setActiveGrammarKey(key);
        const grammarPayload: OptionAWSTranslateDto = {
            text: value[key].value || "",
        };

        requestGrammarPreview(
            value,
            (payload) => grammarApiMethod(client, postOptions).execute(payload),
            (res) => {
            setStep("preview");
            setReviewChecked(false);
            setTimeout(
                () => {
                    const preview = document.querySelector(".modal-preview-focus") as HTMLElement | null;
                    if (preview) preview.focus();
                },
                50
            );
            },
            grammarPayload
        );
        }
    };
    React.useEffect(
        () => {
            if (refInitialFocus.current && props.value && props.focusKey) {
                try {
                    const textLength = props.value[props.focusKey].value.length;
                    refInitialFocus.current.ref.setSelectionRange(textLength, textLength);
                    refInitialFocus.current.focus();
                } catch (e) {
                    console.error("Error setting focus in QuillEditor:", e);
                }
            }
            document.addEventListener("click", handleMouseRelease);
            return () => {
                document.removeEventListener("click", handleMouseRelease);
            };
        },
        []
    );

    // Call the generic preview handler and update step on success
    const handleTranslatesPreview = () => {
        setIsSuggestionMode(false);
        if (props.client && props.postOptions && props.apiMethod) {
            requestPreview(
                props.value,
                (payload) => props.apiMethod!(props.client, props.postOptions).execute(payload),
                (res) => {
                    setStep("preview");
                    setReviewChecked(false);
                    setTimeout(
                        () => {
                            const preview = document.querySelector(".modal-preview-focus") as HTMLElement | null;
                            if (preview) preview.focus();
                        },
                        50
                    );
                }
            );
        }
    };

    const handleSuggestionPreview = () => {
        if (props.client && props.postOptions && props.suggestionApiMethod) {
            requestSuggestionPreview(
                props.objectId,
                (payload) => props.suggestionApiMethod!(props.client, props.postOptions).execute(payload),
                (res) => {
                    setIsSuggestionMode(true);
                    setStep("preview");
                    setReviewChecked(false);
                    setTimeout(
                        () => {
                            const preview = document.querySelector(".modal-preview-focus") as HTMLElement | null;
                            if (preview) preview.focus();
                        },
                        50
                    );
                }
            );
        }
    };

    const getAiMetaByEditingColumn =(editingColumn?: string): IAiMeta | undefined => {
        switch (editingColumn) {
            case "Longtext":
            case 'Langtext':
            return { aiGeneratedLongtext: true };

            case "Footer":
            return { aiGeneratedFooter: true };

            case "USP":
            return { aiGeneratedUsp: true };

            default:
            return undefined;
        }
    };

    const renderHeader = () => {
        const {dialogHeader} = props;
        if (!dialogHeader) return null;
        return (
            <Modal.Header>
                <div className="display--flex justify-content--space-between">
                    <span>
                        {dialogHeader.dialogTitle + " - "}
                        <span style={{color: "#950014"}}>{dialogHeader.optionCode}</span>
                        {" - "}
                        <span style={{color: "#950014"}}>{dialogHeader.optionType}</span>
                    </span>
                    <span>{dialogHeader.editingColumn}</span>
                </div>
                <div className="display--flex justify-content--space-between">
                    <span>{dialogHeader.pdipText}</span>
                </div>
            </Modal.Header>
        );
    };

    const renderEditArea = () => {
        const {value, readOnly, focusKey, onChange, isEditor, longTextSuggestion} = props;
        const isQuillEmpty = (html: string | undefined | null) => {
            if (!html) return true;
            const cleanText = html.replace(/<[^>]*>/g, '').trim();
            return cleanText === "";
        };

        return (
            <Modal.Content
                className="modal-text-area-content"
                style={{height: props.isNewGrid ? "100%" : "80%"}}
            >
                {value && Object.keys(value).map((key) => {
                    const contentValue = value[key] && value[key].value;
                    const isFieldEmpty = isQuillEmpty(contentValue);
                    const isProcessing = grammarLoading || translateLoading || suggestionLoading;

                    return (
                        <div
                            key={"popup-textarea-" + key}
                            className="modal-text-area-tile"
                            onMouseDown={() => props.closeOnDimmerClick(false)}
                        >
                            <div className="modal-text-area-header">
                                <Label basic={true} className="flag-label">
                                    <Flag name={value[key].flag} />
                                </Label>
                                {value[key].flag === "de" && !readOnly && (
                                    <div style={{ display: "flex", gap: "5px", marginLeft: "10px" }}>
                                        {/* Suggestion Button */}
                                        {longTextSuggestion && (
                                            <TranslateIconButton
                                                type="suggestion"
                                                loading={suggestionLoading}
                                                value="ai-suggestion"
                                                onClick={handleSuggestionPreview}
                                                tooltipMessage={tooltipAiGenerate}
                                                disabled={isProcessing}
                                            />
                                        )}
                                        <TranslateIconButton
                                            type="translate"
                                            loading={translateLoading}
                                            value={contentValue}
                                            onClick={handleTranslatesPreview}
                                            disabled={isProcessing || isFieldEmpty}
                                            tooltipMessage={tooltipAiTranslate}
                                        />
                                    </div>
                                )}

                                {(value[key].flag === "de" || value[key].flag === "gb") && !readOnly && (
                                    <TranslateIconButton
                                        type="grammar"
                                        loading={grammarLoading && activeGrammarKey === key}
                                        value={contentValue}
                                        onClick={() => handleGrammarCheck(key)}
                                        disabled={isProcessing || isFieldEmpty}
                                        tooltipMessage={tooltipAiGrammar}
                                    />
                                )}
                            </div>
                            <div className="quill-editor-wrapper">
                                <QuillEditor
                                    keyName={key}
                                    value={
                                        contentValue && !/^<.*?>.*<\/.*?>$/.test(contentValue)
                                            ? plainTextToQuillHtml(contentValue)
                                            : contentValue || ""
                                    }
                                    readOnly={readOnly}
                                    onChange={(keyName: string, newValue: string) => {
                                        if (!readOnly) {
                                            onChange(keyName, newValue);
                                        }
                                    }}
                                    isEditor={isEditor}
                                    ref={key === focusKey ? refInitialFocus : undefined}
                                />
                            </div>
                        </div>
                    );
                })}
            </Modal.Content>
        );
    };
    const isPreviewGrammar = activeGrammarKey !== null;
    return (
        <>
            {props.dialogHeader && renderHeader()}
            {step === "edit" ? renderEditArea() : (
                <PreviewScreen
                    value={props.value}
                    apiResult={isPreviewGrammar ? grammarResult : (translateResult ? translateResult : suggestionResult )}
                    loading={isPreviewGrammar ? grammarLoading : (translateLoading ? translateLoading : suggestionLoading)}
                    error={isPreviewGrammar ? grammarError : (translateError? translateError : suggestionError)}
                    reviewChecked={reviewChecked}
                    isNewGrid={props.isNewGrid}
                    handleRequestPreview={handleTranslatesPreview}
                    handleSuggestionPreview={handleSuggestionPreview}
                    grammarKey={activeGrammarKey}
                    generateSuggestion={isSuggestionMode}
                    onBack={() => {
                        setActiveGrammarKey(null);
                        setIsSuggestionMode(false);
                        handleBackToEdit(setStep, setReviewChecked);
                    }}
                    onConfirm={(previewText) => {
                        const targetKey = activeGrammarKey !== null ? activeGrammarKey : (isSuggestionMode ? getDeKey() : getGbKey());
                        handleConfirm(
                        previewText, props.onChange, setStep, setReviewChecked,
                        getAiMetaByEditingColumn(props.dialogHeader ? props.dialogHeader.editingColumn : undefined), targetKey);
                        setActiveGrammarKey(null);
                        setIsSuggestionMode(false);
                    }}
                    onCheckReview={setReviewChecked}
                    previewDialogHeaders={{
                        dialogheader: previewAiHeader,
                        subHeader: previewAiSubHeader,
                        originalLabel: previewAiOriginal,
                        previewLabel: previewAiPreview,
                        confirmLabel: previewAiConfirm,
                        cancelLabel: previewAiCancel,
                        confirmChangeLabel: previewAiConfimChange,
                        grammarSubHeader: previewAiGrammarSubHeader,
                        generateSubHeader: previewAiGenerateSubHeader
                    }}
                />
            )}
        </>
    );
};

export default ModalTextArea;



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/ModalTextArea.tsx